import { Box, VStack } from "@chakra-ui/react"
import BlankSlate from "@pathwright/ui/src/components/blank/BlankSlate"
import LoadingCircle from "@pathwright/ui/src/components/loading/LoadingCircle"
import defaults from "lodash/defaults"
import { NoteFragment, NoteVisibility } from "@pathwright/graphql-client/src/generated"
import { DumbNoteItem } from "../../note/components/NoteListItem"
import useNotes, { ContextInput } from "../../note/useNotes"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useReviewContext } from "../state/ReviewContextProvider"
import { isUsersCompletion, useUserCanReviewForUser } from "../utils"

const Notes = ({ notes }: { notes: NoteFragment[] }) => (
  <VStack w="full">
    {notes.map((note) => (
      <DumbNoteItem
        key={note.id}
        note={note}
        isExpanded={true}
        canTruncate={false}
        notePermissions={{
          // We aren't supporting any note actions in inbox notes.
          canUpdate: false,
          canDelete: false,
          canViewFullAuthorName: false
        }}
      />
    ))}
  </VStack>
)

// Derives the notes contextInput either from props or
// current review context.
const useNoteContextInput = (contextInput?: ContextInput) => {
  const reviewContext = useReviewContext()
  const step = reviewContext.selected ? reviewContext.selected.data : null
  const reviewNoteContext = step
    ? {
        resource_id: step.context?.resource_id,
        cohort_id: step.context?.cohort_id,
        // NOTE: must scope to the step source_id.
        path_item_source_id: step.source_id
      }
    : null

  return defaults(contextInput, reviewNoteContext)
}

export const useInboxNotes = (contextInput?: ContextInput) => {
  const pwContext = usePathwrightContext()
  const { selected } = useReviewContext()
  const userCanReviewForUser = useUserCanReviewForUser(selected!)
  const context = useNoteContextInput(contextInput)

  // Query notes if user can review for the user or user does not own completion
  // (which infers they can view staff notes since the only non-"staff" user in this
  // context should be the owner of the completion (usually with only learner perms).
  const canQueryNotes =
    userCanReviewForUser.canReview || !isUsersCompletion(selected!, pwContext)

  return useNotes({
    variables: {
      context,
      visibility: NoteVisibility.Staff
    },
    skip: !canQueryNotes
  })
}

const InboxNotes = ({ contextInput }: { contextInput: ContextInput }) => {
  const { notes } = useInboxNotes(contextInput)

  return (
    <Box p={4}>
      {notes ? (
        notes.length ? (
          <Notes notes={notes} />
        ) : (
          // TODO: get approved blank slate.
          <BlankSlate heading="There are no notes available." />
        )
      ) : (
        <LoadingCircle />
      )}
    </Box>
  )
}

export default InboxNotes
